import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import JSZip from "jszip";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchStudents } from "../../redux/studentSlice";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import IdCard from "../../components/IdCard";
import { classList, sectionList, houseList } from "../../utils";
import { formatDate } from "../../functions";
import { saveAs } from "file-saver";
import PrintingIdCard from "../../components/PrintingIdCard";
import CharacterCertificatePage from "../../components/CharacterCertificate";

function StudentDetails() {
  const dispatch = useDispatch();
  const { studentDetails, loading, error } = useSelector(
    (state) => state.students,
  );

  const toUpper = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).toUpperCase();
  };

  const formatRowUppercase = (student) => {
    const row = {};

    Object.entries(columnMapping).forEach(([key, header]) => {
      let value = getValueByPath(student, key);

      if (Array.isArray(value)) {
        value = value.join(", ");
      }

      if (
        (key === "studentDetails.dateOfBirth" ||
          key === "studentDetails.admissionDate") &&
        value
      ) {
        value = formatDate(value);
      }

      row[toUpper(header)] = toUpper(value);
    });

    return row;
  };

  useEffect(() => {
    dispatch(fetchStudents());
  }, [dispatch]);

  const [selectedClass, setSelectedClass] = useState("Uday");
  const [selectedSection, setSelectedSection] = useState("B");
  const [isDownloading, setIsDownloading] = useState(false);
  const [exportType, setExportType] = useState("pdf");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const today = new Date().toISOString().split("T")[0];

  const handleDownloadIDCard = (student) => {
    IdCard(student);
  };

  const handleDownloadMultipleIDCard = async () => {
    const filteredStudents = studentDetails.filter(
      (student) =>
        student.currentClass === selectedClass &&
        student.studentDetails.currentSection === selectedSection,
    );

    if (filteredStudents.length === 0) {
      toast.error("No students Found", {
        position: "top-right",
      });
      return;
    }

    try {
      setIsDownloading(true);
      const sortedStudents = filteredStudents.sort((a, b) => {
        return a.studentDetails.rollNo - b.studentDetails.rollNo;
      });

      await IdCard(sortedStudents);

      const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) {
        closeButton.click();
      }
      setSelectedClass("");
      setSelectedSection("");
    } catch (error) {
      toast.error("Something Went Wrong", {
        position: "top-right",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadMultipleIDCardExcel = async () => {
    const filteredStudents = studentDetails.filter(
      (student) =>
        student.currentClass === selectedClass &&
        student.studentDetails.currentSection === selectedSection,
    );

    if (filteredStudents.length === 0) {
      toast.error("No students Found", {
        position: "top-right",
      });
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const [year, month, day] = dateStr.split("-");
      return `${day}-${month}-${year}`;
    };

    const toUpper = (value) =>
      value !== null && value !== undefined ? String(value).toUpperCase() : "";

    const dataForExcel = filteredStudents
      .slice()
      .sort(
        (a, b) =>
          Number(a.studentDetails.rollNo) - Number(b.studentDetails.rollNo),
      )
      .map((student) => {
        const busStop = student.transportFacility?.busStop;
        const busNumber = student.transportFacility?.busNumber;

        return {
          "Admission No": toUpper(student.admission_no),
          "Student Name": toUpper(student.studentDetails.name),
          Class: toUpper(student.currentClass),
          Section: toUpper(student.studentDetails.currentSection),
          "Roll No": toUpper(student.studentDetails.rollNo),
          "Father's Name": toUpper(student.parentDetails[0]?.name),
          "Mother's Name": toUpper(student.parentDetails[1]?.name),
          "Phone No": toUpper(student.studentDetails.phoneNo),
          "Date of Birth": formatDate(student.studentDetails.dateOfBirth),
          "Bus Stop": toUpper(busStop || "NONE"),
          "Bus Number": busNumber ? toUpper(busNumber) : "0",
          "Blood Group": toUpper(student.studentDetails.bloodGroup),
          House: toUpper(student.studentDetails.house),
          Address: toUpper(student.presentAddress),
        };
      });

    const worksheet = XLSX.utils.json_to_sheet([]);

    const headerRow = [
      [`${selectedClass.toUpperCase()} ${selectedSection.toUpperCase()}`],
    ];
    XLSX.utils.sheet_add_aoa(worksheet, headerRow, { origin: "A1" });
    XLSX.utils.sheet_add_json(worksheet, dataForExcel, {
      origin: "A3",
      skipHeader: false,
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ID Cards");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const filename = `ID_Cards_${selectedClass}_${selectedSection}.xlsx`;
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, filename);
  };

  const handleDownloadClassWiseIDCard = async () => {
    const filteredStudents = studentDetails.filter(
      (student) => student.currentClass === selectedClass,
    );

    if (filteredStudents.length === 0) {
      toast.error("No students Found", {
        position: "top-right",
      });
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const [year, month, day] = dateStr.split("-");
      return `${day}-${month}-${year}`;
    };

    const toUpper = (value) =>
      value !== null && value !== undefined ? String(value).toUpperCase() : "";

    // Group by section
    const sectionGroups = {};
    filteredStudents.forEach((student) => {
      const section =
        student.studentDetails.currentSection?.toUpperCase() || "UNKNOWN";
      if (!sectionGroups[section]) {
        sectionGroups[section] = [];
      }
      sectionGroups[section].push(student);
    });

    const workbook = XLSX.utils.book_new();

    // Create one sheet per section
    Object.entries(sectionGroups).forEach(([section, students]) => {
      const sortedStudents = students
        .slice()
        .sort(
          (a, b) =>
            Number(a.studentDetails.rollNo) - Number(b.studentDetails.rollNo),
        );

      const dataForExcel = sortedStudents.map((student) => {
        const busStop = student.transportFacility?.busStop;
        const busNumber = student.transportFacility?.busNumber;

        return {
          "Admission No": toUpper(student.admission_no),
          "Student Name": toUpper(student.studentDetails.name),
          Class: toUpper(student.currentClass),
          Section: section,
          "Roll No": toUpper(student.studentDetails.rollNo),
          "Father's Name": toUpper(student.parentDetails[0]?.name),
          "Mother's Name": toUpper(student.parentDetails[1]?.name),
          "Phone No": toUpper(student.studentDetails.phoneNo),
          "Date of Birth": formatDate(student.studentDetails.dateOfBirth),
          "Bus Stop": toUpper(busStop || "NONE"),
          "Bus Number": busNumber ? toUpper(busNumber) : "0",
          "Blood Group": toUpper(student.studentDetails.bloodGroup),
          House: toUpper(student.studentDetails.house),
          Address: toUpper(student.presentAddress),
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
      XLSX.utils.book_append_sheet(workbook, worksheet, section); // Sheet named "A", "B", etc.
    });

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const filename = `ID_Cards_${selectedClass}_BySection.xlsx`;
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, filename);
  };

  const handleDownloadPrintingIDCard = async () => {
    const filteredStudents = studentDetails.filter(
      (student) => student.currentClass === selectedClass,
    );

    if (filteredStudents.length === 0) {
      toast.error("No students Found", {
        position: "top-right",
      });
      return;
    }

    try {
      setIsDownloading(true);
      const sortedStudents = filteredStudents.sort((a, b) => {
        return a.studentDetails.rollNo - b.studentDetails.rollNo;
      });

      await PrintingIdCard(sortedStudents);

      const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) {
        closeButton.click();
      }
      setSelectedClass("");
    } catch (error) {
      toast.error("Something Went Wrong", {
        position: "top-right",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownload = () => {
    if (exportType === "pdf") {
      handleDownloadMultipleIDCard();
    } else if (exportType === "excel") {
      handleDownloadMultipleIDCardExcel();
    }
  };

  const flattenObjectValues = (obj) => {
    let values = [];
    for (let key in obj) {
      const value = obj[key];
      if (typeof value === "object" && value !== null) {
        values = values.concat(flattenObjectValues(value));
      } else if (typeof value === "string") {
        values.push(value);
      }
    }
    return values;
  };

  const filteredRecords = studentDetails.filter((student) => {
    const searchLower = searchTerm.toLowerCase();
    const allValues = flattenObjectValues(student);
    return allValues.some((val) => val.toLowerCase().includes(searchLower));
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleDownloadAllImagesZip = async () => {
    toast.info("Preparing ZIP", { position: "top-right" });
    const zip = new JSZip();

    const downloadImageAsBlob = async (url) => {
      try {
        const response = await fetch(url);
        if (!response.ok) return null;
        return await response.blob();
      } catch {
        return null;
      }
    };

    const getExtension = (url) => {
      if (!url) return ".jpg";
      const match = url.split("?")[0].match(/\.(jpg|jpeg|png|webp|gif)$/i);
      return match ? `.${match[1].toLowerCase()}` : ".jpg";
    };

    const sanitizeName = (name) => {
      if (!name || name.trim() === "") return null;
      return name
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "_")
        .replace(/[^A-Z0-9_]/g, "");
    };

    for (const student of studentDetails) {
      const admissionNo =
        student.admission_no ||
        student.studentDetails?.admission_no ||
        "UNKNOWN";
      const folder = zip.folder(admissionNo);

      // 1. Student Image
      const studentName = sanitizeName(student.studentDetails?.name);
      const studentImgUrl = student.studentDetails?.studentImage;
      if (studentImgUrl && studentName) {
        const blob = await downloadImageAsBlob(studentImgUrl);
        if (blob) {
          const ext = getExtension(studentImgUrl);
          folder.file(`${studentName}_student${ext}`, blob);
        }
      }

      // 2. Parent Images
      const relationLabels = ["father", "mother", "guardian1", "guardian2"];

      for (let i = 0; i < student.parentDetails.length; i++) {
        const parent = student.parentDetails[i];
        const parentImgUrl = parent?.image;
        const parentName = sanitizeName(parent?.name);

        if (parentImgUrl && parentName) {
          const blob = await downloadImageAsBlob(parentImgUrl);
          if (blob) {
            const ext = getExtension(parentImgUrl);
            const label = relationLabels[i] || `parent${i + 1}`;
            folder.file(`${parentName}_${label}${ext}`, blob);
          }
        }
      }
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(zipBlob, "STUDENT_IMAGES.zip");
    toast.success("ZIP Downloaded", { position: "top-right" });
  };

  const handleDownloadImageReportExcel = () => {
    if (!studentDetails || studentDetails.length === 0) {
      toast.error("No students Found", { position: "top-right" });
      return;
    }

    const workbook = XLSX.utils.book_new();

    // group by class, then by section
    const classGroups = {};
    studentDetails.forEach((student) => {
      const cls = student.currentClass || "UNKNOWN";
      const sec = (student.studentDetails?.currentSection || "A").toUpperCase();

      if (!classGroups[cls]) classGroups[cls] = {};
      if (!classGroups[cls][sec]) classGroups[cls][sec] = [];

      classGroups[cls][sec].push(student);
    });

    Object.entries(classGroups).forEach(([className, sections]) => {
      const combinedData = [];

      Object.keys(sections)
        .sort()
        .forEach((sectionName) => {
          const studentsInSection = sections[sectionName]
            .slice()
            .sort(
              (a, b) =>
                Number(a.studentDetails.rollNo) -
                Number(b.studentDetails.rollNo),
            );

          // Section header row
          combinedData.push({ "Admission No": `SECTION ${sectionName}` });

          studentsInSection.forEach((student) => {
            combinedData.push({
              "Admission No": toUpper(student.admission_no),
              "Student Name": toUpper(student.studentDetails?.name),
              "Roll No": toUpper(student.studentDetails?.rollNo),
              "Student Photo": student.studentDetails?.studentImage || "N/A",
              "Father Photo": student.parentDetails?.[0]?.image || "N/A",
              "Mother Photo": student.parentDetails?.[1]?.image || "N/A",
              "Guardian 1 Photo": student.parentDetails?.[2]?.image || "N/A",
              "Guardian 2 Photo": student.parentDetails?.[3]?.image || "N/A",
            });
          });

          // gap row between sections
          combinedData.push({});
        });

      const worksheet = XLSX.utils.json_to_sheet(combinedData);

      // column widths so URLs are readable
      worksheet["!cols"] = [
        { wch: 16 }, // Admission No
        { wch: 24 }, // Student Name
        { wch: 10 }, // Roll No
        { wch: 45 }, // Student Photo
        { wch: 45 }, // Father Photo
        { wch: 45 }, // Mother Photo
        { wch: 45 }, // Guardian 1 Photo
        { wch: 45 }, // Guardian 2 Photo
      ];

      const safeSheetName = toUpper(className).slice(0, 31); // Excel sheet name limit
      XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);
    });

    XLSX.writeFile(workbook, "Students_Photo_Report.xlsx");
    toast.success("Report Generated");
  };

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const getValueByPath = (obj, path) => {
    return path.split(".").reduce((acc, key) => {
      if (!acc) return "";
      const match = key.match(/(.*?)\[(\d+)\]/);
      if (match) {
        const arrayKey = match[1];
        const index = parseInt(match[2], 10);
        return acc[arrayKey] && acc[arrayKey][index] !== undefined
          ? acc[arrayKey][index]
          : "";
      }
      return acc[key] !== undefined ? acc[key] : "";
    }, obj);
  };

  const columnMapping = {
    studentId: "Student ID",
    admission_no: "Admission No",
    "studentDetails.admissionDate": "Admission Date",
    "studentDetails.rollNo": "Roll No",
    "studentDetails.apaarId": "Apaar ID",
    "studentDetails.pen": "PEN",
    "studentDetails.name": "Student Name",
    "studentDetails.gender": "Gender",
    currentClass: "Class",
    "studentDetails.currentSection": "Section",
    "studentDetails.dateOfBirth": "Date of Birth",
    "studentDetails.phoneNo": "Phone No",
    "studentDetails.house": "House",
    "studentDetails.category": "Category",
    "studentDetails.aadhar_no": "Aadhaar Card No.",
    "studentDetails.bloodGroup": "Blood Group",
    "studentDetails.handicap": "Handicap",
    "transportFacility.busStop": "Bus Stop",
    "feeDetails.transportFee.amt": "Transport Fee",
    "transportFacility.vehicleNo": "Vehicle No",
    "transportFacility.busNumber": "Bus No",
    "parentDetails[0].email": "Email ID",
    "presentAddress.address": "Present Address",
    "parentDetails[0].name": "Father's Name",
    "parentDetails[0].occupation": "Father's Occupation",
    "parentDetails[1].name": "Mother's Name",
    "parentDetails[1].occupation": "Mother's Occupation",
    "parentDetails[2].name": "Guardian 1 Name",
    "parentDetails[2].phone": "Guardian 1 Phone",
    "parentDetails[2].guardianAadhaar": "Guardian 1 Aadhaar",
    "parentDetails[3].name": "Guardian 2 Name",
    "parentDetails[3].phone": "Guardian 2 Phone",
    "parentDetails[3].guardianAadhaar": "Guardian 2 Aadhaar",
  };

  const handleExportExcel = () => {
    const workbook = XLSX.utils.book_new();

    const formattedData = studentDetails.map(formatRowUppercase);

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "ALL_STUDENTS");

    XLSX.writeFile(workbook, "STUDENTS.xlsx");
    toast.success("Report Generated");
  };

  const handleExportClassWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const classGroups = {};

    studentDetails.forEach((student) => {
      const cls = student.currentClass || "UNKNOWN";
      const sec = student.studentDetails?.currentSection || "A";

      if (!classGroups[cls]) classGroups[cls] = {};
      if (!classGroups[cls][sec]) classGroups[cls][sec] = [];

      classGroups[cls][sec].push(formatRowUppercase(student));
    });

    Object.entries(classGroups).forEach(([className, sections]) => {
      const combinedData = [];

      Object.keys(sections)
        .sort()
        .forEach((sec) => {
          const students = sections[sec];

          students.sort((a, b) => Number(a["ROLL NO"]) - Number(b["ROLL NO"]));

          combinedData.push({ SECTION: `SECTION ${sec}` });
          combinedData.push(...students);
          combinedData.push({});
        });

      const worksheet = XLSX.utils.json_to_sheet(combinedData);
      XLSX.utils.book_append_sheet(workbook, worksheet, toUpper(className));
    });

    XLSX.writeFile(workbook, "STUDENT_CLASSWISE.xlsx");
    toast.success("Report Generated");
  };

  const handleExportWalkerStudentsExcel = () => {
    const workbook = XLSX.utils.book_new();

    const walkerStudents = studentDetails.filter(
      (student) => !getValueByPath(student, "transportFacility.busStop"),
    );

    if (walkerStudents.length === 0) {
      toast.error("No walker students found");
      return;
    }

    const formattedData = walkerStudents
      .map(formatRowUppercase)
      .sort((a, b) => Number(a["ROLL NO"]) - Number(b["ROLL NO"]));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "WALKER_STUDENTS");

    XLSX.writeFile(workbook, "STUDENTS_WALKER.xlsx");
    toast.success("Report Generated");
  };

  const handleExportTransportAvailingStudentsExcel = () => {
    const workbook = XLSX.utils.book_new();

    const transportStudents = studentDetails.filter((student) =>
      getValueByPath(student, "transportFacility.busStop"),
    );

    if (transportStudents.length === 0) {
      toast.error("No transport students found");
      return;
    }

    const formattedData = transportStudents
      .map(formatRowUppercase)
      .sort((a, b) => Number(a["ROLL NO"]) - Number(b["ROLL NO"]));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "TRANSPORT_STUDENTS");

    XLSX.writeFile(workbook, "STUDENTS_AVAILING_TRANSPORT.xlsx");
    toast.success("Report Generated");
  };

  const handleDownloadBirthdayReport = () => {
    const workbook = XLSX.utils.book_new();

    const today = new Date();
    const todayMonthDay = `${String(today.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(today.getDate()).padStart(2, "0")}`;

    const birthdayStudents = studentDetails.filter((student) => {
      const dob = getValueByPath(student, "studentDetails.dateOfBirth");
      if (!dob) return false;
      const [, month, day] = dob.split("-");
      return `${month}-${day}` === todayMonthDay;
    });

    if (!birthdayStudents.length) {
      toast.error("No birthdays today");
      return;
    }

    const formattedData = birthdayStudents
      .map(formatRowUppercase)
      .sort((a, b) => Number(a["ROLL NO"]) - Number(b["ROLL NO"]));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "TODAYS_BIRTHDAYS");

    XLSX.writeFile(workbook, `STUDENTS_BIRTHDAY.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportHouseWiseExcel = () => {
    const workbook = XLSX.utils.book_new();

    houseList.forEach((house) => {
      const studentsInHouse = studentDetails.filter(
        (student) => student?.studentDetails?.house === house,
      );

      if (!studentsInHouse.length) return;

      const formattedData = studentsInHouse
        .map(formatRowUppercase)
        .sort((a, b) => Number(a["ROLL NO"]) - Number(b["ROLL NO"]));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      XLSX.utils.book_append_sheet(workbook, worksheet, toUpper(house));
    });

    XLSX.writeFile(workbook, `STUDENTS_HOUSEWISE.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportBusWiseExcel = () => {
    const workbook = XLSX.utils.book_new();

    const busNos = [
      ...new Set(
        studentDetails
          .map((s) => s?.transportFacility?.busNumber)
          .filter(Boolean),
      ),
    ];

    busNos.forEach((busNo) => {
      const students = studentDetails.filter(
        (s) => s?.transportFacility?.busNumber === busNo,
      );

      const formattedData = students
        .map(formatRowUppercase)
        .sort((a, b) => Number(a["ROLL NO"]) - Number(b["ROLL NO"]));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      XLSX.utils.book_append_sheet(workbook, worksheet, toUpper(busNo));
    });

    XLSX.writeFile(workbook, `STUDENTS_BUSWISE.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportGenderWiseExcel = () => {
    const workbook = XLSX.utils.book_new();
    const groups = {};

    studentDetails.forEach((student) => {
      const gender = toUpper(student?.studentDetails?.gender || "UNKNOWN");

      if (!groups[gender]) groups[gender] = [];

      groups[gender].push(formatRowUppercase(student));
    });

    Object.entries(groups).forEach(([gender, students]) => {
      students.sort((a, b) => Number(a["ROLL NO"]) - Number(b["ROLL NO"]));

      const worksheet = XLSX.utils.json_to_sheet(students);
      XLSX.utils.book_append_sheet(workbook, worksheet, gender);
    });

    XLSX.writeFile(workbook, `STUDENTS_GENDERWISE.xlsx`);
    toast.success("Report Generated");
  };

  const handleDownloadStudentSummaryReport = (data) => {
    if (!data || data.length === 0) {
      toast.error("No student data available.");
      return;
    }

    const getCategory = (student) => {
      const cat = student?.studentDetails?.category || "NONE";
      const normalized = cat.toUpperCase().trim();
      const validCats = [
        "ST",
        "SC",
        "OBC",
        "GENERAL",
        "PHYSICALLY DISABLED",
        "NONE",
      ];
      return validCats.includes(normalized) ? normalized : "NONE";
    };

    // Group by class
    const classGroups = {};
    data.forEach((item) => {
      const cls = item.studentDetails?.currentClass || "Unspecified";
      if (!classGroups[cls]) {
        classGroups[cls] = {
          total: 0,
          boys: 0,
          girls: 0,
          ST: 0,
          SC: 0,
          OBC: 0,
          GENERAL: 0,
          "PHYSICALLY DISABLED": 0,
          NONE: 0,
          transport: 0,
        };
      }

      const gender = item.studentDetails?.gender?.toLowerCase();
      const category = getCategory(item);
      const hasTransport = item.transportFacility ? true : false;
      const handicap = item.studentDetails?.handicap?.toLowerCase() === "yes";

      classGroups[cls].total += 1;
      if (gender === "male") classGroups[cls].boys += 1;
      if (gender === "female") classGroups[cls].girls += 1;
      if (hasTransport) classGroups[cls].transport += 1;

      if (handicap) classGroups[cls]["PHYSICALLY DISABLED"] += 1;
      else classGroups[cls][category] += 1;
    });

    // Convert grouped data to Excel rows
    const rows = Object.entries(classGroups).map(([cls, stats]) => ({
      Class: cls,
      "Total Students": stats.total,
      Boys: stats.boys,
      Girls: stats.girls,
      ST: stats.ST,
      SC: stats.SC,
      OBC: stats.OBC,
      General: stats.GENERAL,
      "Physically Disabled": stats["PHYSICALLY DISABLED"],
      None: stats.NONE,
      Transport: stats.transport,
    }));

    // Add grand total at the bottom
    const totals = rows.reduce(
      (acc, curr) => {
        Object.keys(acc).forEach((k) => {
          if (k !== "Class") acc[k] += curr[k];
        });
        return acc;
      },
      {
        Class: "TOTAL",
        "Total Students": 0,
        Boys: 0,
        Girls: 0,
        ST: 0,
        SC: 0,
        OBC: 0,
        General: 0,
        "Physically Disabled": 0,
        None: 0,
        Transport: 0,
      },
    );

    rows.push(totals);

    // Create Excel
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Student Summary");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, "Student_Summary_Report.xlsx");
    toast.success("Student Summary Report Generated");
  };

  const handleExportSessionWiseExcel = () => {
    const workbook = XLSX.utils.book_new();
    const groups = {};

    studentDetails.forEach((student) => {
      const session = toUpper(student.session || "UNKNOWN");

      if (!groups[session]) groups[session] = [];

      groups[session].push(formatRowUppercase(student));
    });

    Object.entries(groups).forEach(([session, students]) => {
      const worksheet = XLSX.utils.json_to_sheet(students);
      XLSX.utils.book_append_sheet(workbook, worksheet, session);
    });

    XLSX.writeFile(workbook, "SESSION_WISE_ADMISSION_REPORT.xlsx");
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((student, index) => (
                          <tr key={index}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Student ID</span> &mdash;{" "}
                              {student.studentId}
                              <br />
                              <span class="text-dark">Admission No</span>{" "}
                              &mdash; {student.studentDetails.admission_no}{" "}
                              <br />
                              <span class="text-dark">Admission Date</span>{" "}
                              &mdash;{" "}
                              {formatDate(student.studentDetails.admissionDate)}{" "}
                              <br />
                              <span class="text-dark">Student Name</span>{" "}
                              &mdash; {student.studentDetails.name} <br />
                              <span class="text-dark">Gender</span> &mdash;{" "}
                              {student.studentDetails.gender}
                              <br />
                              <span class="text-dark">Class</span> &mdash;{" "}
                              {student.currentClass} <br />
                              <span class="text-dark">
                                Section
                              </span> &mdash;{" "}
                              {student.studentDetails.currentSection} <br />
                              <span class="text-dark">
                                Roll No
                              </span> &mdash; {student.studentDetails.rollNo}{" "}
                              <br />
                              <span class="text-dark">House</span> &mdash;{" "}
                              {student.studentDetails.house} <br />
                              <span class="text-dark">Date of Birth</span>{" "}
                              &mdash;{" "}
                              {formatDate(student.studentDetails.dateOfBirth)}
                              <br />
                              <span class="text-dark">
                                Blood Group
                              </span> &mdash;{" "}
                              {student.studentDetails.bloodGroup}
                              <br />
                              <span class="text-dark">
                                Aadhaar Card No
                              </span>{" "}
                              &mdash; {student.studentDetails.aadhar_no}
                              <br />
                              <span class="text-dark">
                                Handicap
                              </span> &mdash; {student.studentDetails.handicap}
                              <br />
                              <span class="text-dark">
                                Phone No
                              </span> &mdash; {student.studentDetails.phoneNo}
                              <hr className="w-25" />
                              <span class="text-dark">
                                Stoppage
                              </span> &mdash;{" "}
                              <span className="text-capitalize">
                                {student.transportFacility?.busStop || "NONE"}
                              </span>
                              <br />
                              <span class="text-dark">
                                Vehicle No
                              </span> &mdash;{" "}
                              {student.transportFacility?.vehicleNo || "N/A"}
                              <br />
                              <span class="text-dark">Bus No</span> &mdash;{" "}
                              {student.transportFacility?.busNumber || "N/A"}
                              <br />
                              <span class="text-dark">Transport Fee</span>{" "}
                              &mdash;{" "}
                              {student.feeDetails?.transportFee?.amt || "N/A"}
                              <br />
                              <div className="mt-3">
                                <CharacterCertificatePage student={student} />
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <div className="d-flex align-items-center bg-light rounded-4 mb-2">
                                  <div className="p-2 bg-dark rounded-4 opacity-75">
                                    {!student.transportFacility.busStop ? (
                                      <img
                                        src="/assets/images/walker.png"
                                        width="22"
                                      />
                                    ) : (
                                      <img
                                        src="/assets/images/transport.png"
                                        width="22"
                                      />
                                    )}
                                  </div>
                                  <div className="ps-3">
                                    <p className="mb-0 lh-1 fs-5 text-center text-dark">
                                      {!student.transportFacility.busStop
                                        ? "Walker"
                                        : "Bus"}
                                    </p>
                                  </div>
                                </div>

                                <Link
                                  to="/view-student"
                                  state={{
                                    admissionNo:
                                      student.studentDetails.admission_no,
                                  }}
                                  class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  View Details
                                </Link>
                                <Link
                                  to="/update-student"
                                  state={{
                                    admissionNo:
                                      student.studentDetails.admission_no,
                                  }}
                                  class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Update Details
                                </Link>
                                <Link
                                  to="/assign-view-incidents"
                                  state={{
                                    admissionNo:
                                      student.studentDetails.admission_no,
                                  }}
                                  class="badge bg-dark-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Behaviour Records
                                </Link>
                                <Link
                                  to="/update-transport"
                                  state={{
                                    admissionNo:
                                      student.studentDetails.admission_no,
                                  }}
                                  class="badge bg-info-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Transport
                                </Link>
                                <Link
                                  to="/update-scholarship"
                                  state={{
                                    admissionNo:
                                      student.studentDetails.admission_no,
                                  }}
                                  class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Scholarship
                                </Link>
                                <Link
                                  to="/collect-fees"
                                  state={{
                                    admissionNo:
                                      student.studentDetails.admission_no,
                                  }}
                                  class="badge bg-warning-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Collect Fees
                                </Link>
                                <Link
                                  to="/student-attendance"
                                  state={{
                                    admissionNo:
                                      student.studentDetails.admission_no,
                                  }}
                                  class="badge bg-dark-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Attendance
                                </Link>
                                <Link
                                  to="/book-issue-return-student"
                                  state={{
                                    admissionNo:
                                      student.studentDetails.admission_no,
                                  }}
                                  class="badge bg-dark-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Book Issue / Return
                                </Link>
                                <div className="d-flex justify-content-center">
                                  <hr className="w-50" />
                                </div>
                                <Link
                                  className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  onClick={() => handleDownloadIDCard(student)}
                                >
                                  Download ID Card
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          to="/add-student"
                          class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add Student
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Student Information</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Student Details
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div class="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                        <div class="avatar-group">
                          {[1, 2, 3, 4, 5].map((num) => (
                            <span className="avatar avatar-md" key={num}>
                              <img
                                alt={`avatar-${num}`}
                                src={`/assets/images/students/${num}.jpg`}
                                className="rounded-circle"
                              />
                            </span>
                          ))}
                        </div>
                        <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                          {studentDetails.length.toLocaleString("en-IN")}
                        </h3>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Student Details
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              5.4 MB Size
                            </span>
                          </p>
                        </div>
                        <span
                          onClick={handleExportExcel}
                          className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                        >
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportClassWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Class/Section Wise
                                </span>
                                <span
                                  onClick={handleDownloadBirthdayReport}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Birthday Today
                                </span>
                                <span
                                  onClick={handleExportHouseWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  House - Wise Report
                                </span>
                                <span
                                  onClick={handleExportBusWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Bus No Wise
                                </span>
                                <span
                                  onClick={handleExportGenderWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Gender Wise
                                </span>
                                <span
                                  onClick={() =>
                                    handleDownloadStudentSummaryReport(
                                      studentDetails,
                                    )
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Student's Summary Report
                                </span>
                                <span
                                  onClick={handleExportSessionWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Session Wise Admission Report
                                </span>
                                <span
                                  onClick={handleDownloadAllImagesZip}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Download All Images (ZIP)
                                </span>
                                <span
                                  onClick={handleDownloadImageReportExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Student Photo Report (Excel)
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>

                      <div class="mt-1">
                        <div className="row g-1">
                          <div className="col-6">
                            <div
                              data-bs-toggle="modal"
                              data-bs-target="#exampleModal"
                              className="p-3 bg-success-soft rounded-4 cursor-pointer"
                              onClick={() => setExportType("pdf")}
                            >
                              <p className="m-0 text-center fw-medium lh-sm text-dark">
                                ID Card (PDF)
                              </p>
                              <p className="text-center fs-5 m-0 lh-1">
                                Class / Section Wise
                              </p>
                            </div>
                          </div>
                          <div className="col-6">
                            <div
                              data-bs-toggle="modal"
                              data-bs-target="#exampleModal"
                              className="p-3 bg-success-soft rounded-4 cursor-pointer"
                              onClick={() => setExportType("excel")}
                            >
                              <p className="m-0 text-center fw-medium lh-sm text-dark">
                                ID Card (Excel)
                              </p>
                              <p className="text-center fs-5 m-0 lh-1">
                                Class / Section Wise
                              </p>
                            </div>
                          </div>
                          <div className="col-12">
                            <div
                              data-bs-toggle="modal"
                              data-bs-target="#printingIDModal"
                              className="p-3 bg-info-soft rounded-4 cursor-pointer"
                            >
                              <p className="m-0 text-center fw-medium lh-sm text-dark">
                                Printing ID Card (PDF)
                              </p>
                              <p className="text-center fs-5 m-0 lh-1">
                                Class Wise
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="mt-1">
                        <div className="row g-1">
                          <div className="col-6">
                            <div
                              onClick={handleExportWalkerStudentsExcel}
                              className="p-3 bg-danger-soft rounded-4 cursor-pointer"
                            >
                              <p className="m-0 text-center fw-medium lh-sm text-dark">
                                Walker Students
                              </p>
                              <p className="text-center fs-5 m-0 lh-1">
                                Excel Report
                              </p>
                            </div>
                          </div>
                          <div className="col-6">
                            <div
                              onClick={
                                handleExportTransportAvailingStudentsExcel
                              }
                              className="p-3 bg-danger-soft rounded-4 cursor-pointer"
                            >
                              <p className="m-0 text-center fw-medium lh-sm text-dark">
                                Availing Transport
                              </p>
                              <p className="text-center fs-5 m-0 lh-1">
                                Excel Report
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>

                <div
                  class="modal m-0 fade"
                  id="exampleModal"
                  tabindex="-1"
                  aria-labelledby="exampleModalLabel"
                  aria-hidden="true"
                >
                  <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content rounded-5 overflow-hidden">
                      <div class="p-4 bg-danger-soft d-flex justify-content-end">
                        <i
                          className="bi bi-x-lg text-dark fs-3 cursor-pointer position-relative z-1"
                          data-bs-dismiss="modal"
                          aria-label="Close"
                        ></i>
                      </div>
                      <div class="pt-0 p-5">
                        <div className="d-flex justify-content-center mt-n8">
                          <img
                            src="/assets/images/report.jpg"
                            width="90"
                            className="object-fit-contain p-3 rounded-5 bg-white"
                          />
                        </div>

                        <div className="mt-2">
                          <h5 class="text-center lh-1 fw-medium">
                            Class / Section Wise ID Card <br />
                            <span className="text-secondary">
                              Generate ID Card
                            </span>
                          </h5>
                        </div>

                        <div className="mt-4">
                          <div className="bg-light rounded-pill py-2 mb-3">
                            <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                              Select Class
                            </h5>
                          </div>

                          <div className="d-flex flex-wrap justify-content-center gap-1 px-4">
                            {classList.map((cls, index) => (
                              <div
                                key={index}
                                className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 ${
                                  selectedClass === cls
                                    ? "bg-warning text-white border-warning"
                                    : ""
                                }`}
                                onClick={() => setSelectedClass(cls)}
                              >
                                {cls}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mt-4">
                          <div className="bg-light rounded-pill py-2 mb-3">
                            <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                              Select Section
                            </h5>
                          </div>

                          <div className="d-flex flex-wrap justify-content-center gap-1 px-lg-8 px-5 mx-lg-4">
                            {sectionList.map((sec, index) => (
                              <div
                                key={index}
                                className={`bg-dark-soft rounded-pill cursor-pointer px-2 py-1 fs-6 ${
                                  selectedSection === sec
                                    ? "bg-warning text-white border-warning"
                                    : ""
                                }`}
                                onClick={() => setSelectedSection(sec)}
                              >
                                <span className="mx-1">{sec}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mt-5 d-flex justify-content-center flex-column align-items-center">
                          <div>
                            {isDownloading ? (
                              <div class="bg-primary md-circle-button fw-medium">
                                <div
                                  class="spinner-border spinner-border-md text-white"
                                  role="status"
                                ></div>
                              </div>
                            ) : (
                              <button
                                onClick={handleDownload}
                                class="btn btn-primary rounded-pill px-6 fs-5 fw-medium"
                              >
                                Download{" "}
                                {exportType === "pdf" ? "PDF" : "Excel"} ID Card
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="modal m-0 fade"
                  id="printingIDModal"
                  tabindex="-1"
                  aria-labelledby="exampleModalLabel"
                  aria-hidden="true"
                >
                  <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content rounded-5 overflow-hidden">
                      <div class="p-4 bg-danger-soft d-flex justify-content-end">
                        <i
                          className="bi bi-x-lg text-dark fs-3 cursor-pointer position-relative z-1"
                          data-bs-dismiss="modal"
                          aria-label="Close"
                        ></i>
                      </div>
                      <div class="pt-0 p-5">
                        <div className="d-flex justify-content-center mt-n8">
                          <img
                            src="/assets/images/report.jpg"
                            width="90"
                            className="object-fit-contain p-3 rounded-5 bg-white"
                          />
                        </div>

                        <div className="mt-2">
                          <h5 class="text-center lh-1 fw-medium">
                            Print Class Wise ID Card <br />
                            <span className="text-secondary">
                              Generate ID Card
                            </span>
                          </h5>
                        </div>

                        <div className="mt-4">
                          <div className="bg-light rounded-pill py-2 mb-3">
                            <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                              Select Class
                            </h5>
                          </div>

                          <div className="d-flex flex-wrap justify-content-center gap-1 px-4">
                            {classList.map((cls, index) => (
                              <div
                                key={index}
                                className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 ${
                                  selectedClass === cls
                                    ? "bg-warning text-white border-warning"
                                    : ""
                                }`}
                                onClick={() => setSelectedClass(cls)}
                              >
                                {cls}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mt-5 d-flex justify-content-center flex-column align-items-center">
                          <div>
                            {isDownloading ? (
                              <div class="bg-primary md-circle-button fw-medium">
                                <div
                                  class="spinner-border spinner-border-md text-white"
                                  role="status"
                                ></div>
                              </div>
                            ) : (
                              <button
                                onClick={handleDownloadPrintingIDCard}
                                class="btn btn-primary rounded-pill px-6 fs-5 fw-medium"
                              >
                                Download ID Card
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default StudentDetails;
